import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, OnboardingStatus } from '@/types';
import api from '@/services/api';
import { useWorkspaceStore } from './workspaceStore';
import { usePostsStore } from './postsStore';
import { usePromptStore } from './promptStore';
import { usePreferencesStore } from './preferencesStore';
import { useHistoryStore } from './historyStore';
import { useAnalyticsStore } from './analyticsStore';
import { useCaseStudyStore } from './caseStudyStore';
import { useConsentStore } from './consentStore';
import { useNotificationStore } from './notificationStore';
import { abortActiveStream } from './postsStore';
import { abortScope, bumpEpoch } from '@/features/a0/identity/identity';
import { DEV_MODE } from '@/lib/devMode';

/** Device settings that outlive a logout; every other app key in localStorage belongs to the user. */
const DEVICE_KEYS = ['zupa-theme', 'preferred_currency'];

const leaveSession = () => {
  abortScope('all');
  abortActiveStream();
  bumpEpoch();
};

const sweepUserStorage = () => {
  const keys = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i) as string);
  for (const key of keys) {
    if (DEVICE_KEYS.includes(key) || (DEV_MODE && key.startsWith('dev_'))) continue;
    localStorage.removeItem(key);
  }
};

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  onboardingStatus: OnboardingStatus | null;
  
  // Actions
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  // Auth actions
  login: (email: string, password: string, remember_me?: boolean) => Promise<OnboardingStatus | null>;
  register: (email: string, password: string, full_name: string, accepted_terms: boolean) => Promise<string>;
  logout: () => Promise<void>;
  fetchCurrentUser: () => Promise<void>;
  
  // Onboarding
  fetchOnboardingStatus: () => Promise<OnboardingStatus | null>;
  
  // Clear all
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,
      onboardingStatus: null,

      setUser: (user) => set({ user, isAuthenticated: !!user }),
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),

      login: async (email, password, remember_me = false) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.login({ email, password, remember_me });
          leaveSession();
          set({
            user: response.user,
            isAuthenticated: true,
          });
          
          // Fetch onboarding status to determine where to redirect
          const status = await get().fetchOnboardingStatus();
          
          set({ isLoading: false });
          return status;
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Login failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      register: async (email, password, full_name, accepted_terms) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.register({ email, password, full_name, accepted_terms });
          set({ isLoading: false });
          return response.message;
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Registration failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          await api.logout();
        } catch {
          // Ignore logout errors
        }
        get().clearAuth();
      },

      fetchCurrentUser: async () => {
        const token = localStorage.getItem('access_token');
        if (!token) {
          set({ isLoading: false, isAuthenticated: false });
          return;
        }

        set({ isLoading: true });
        try {
          const user = await api.getCurrentUser();
          set({
            user,
            isAuthenticated: true,
          });
          
          // Fetch onboarding status
          await get().fetchOnboardingStatus();
          
          set({ isLoading: false });
        } catch {
          get().clearAuth();
        }
      },

      fetchOnboardingStatus: async () => {
        try {
          const status = await api.getOnboardingStatus();
          set({ onboardingStatus: status });
          return status;
        } catch {
          // On failure, set a safe default so PrivateRoute isn't stuck in LoadingScreen forever
          set({ onboardingStatus: { is_onboarded: true, has_profile: false, company_name: null, services_count: 0 } });
          return null;
        }
      },

      clearAuth: () => {
        leaveSession();
        localStorage.removeItem('access_token');
        
        // CRITICAL: Clear ALL user-specific stores to prevent data leakage between accounts
        useWorkspaceStore.getState().clearWorkspaces();
        usePostsStore.getState().clearAll();
        usePromptStore.getState().clearPrompts();
        usePreferencesStore.getState().clearPreferences();
        useWorkspaceStore.setState(useWorkspaceStore.getInitialState(), true);
        usePromptStore.setState(usePromptStore.getInitialState(), true);
        usePreferencesStore.setState(usePreferencesStore.getInitialState(), true);
        useHistoryStore.setState(useHistoryStore.getInitialState(), true);
        useAnalyticsStore.setState(useAnalyticsStore.getInitialState(), true);
        useCaseStudyStore.setState(useCaseStudyStore.getInitialState(), true);
        useConsentStore.setState(useConsentStore.getInitialState(), true);
        useNotificationStore.setState(useNotificationStore.getInitialState(), true);
        
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          onboardingStatus: null,
        });
        // Last, so nothing persisted by the resets above survives.
        sweepUserStorage();
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
